import React, { useEffect, useState } from "react";
import { client, fmtTimeAST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LABEL = "font-condensed uppercase tracking-wider text-[11px] font-semibold";
const INPUT = "h-9 w-full rounded-[10px] px-2 text-[13px] bg-white";
const PAUSE_LABELS = {
  break: "Break", "waiting for parts": "Waiting for parts",
  "waiting for customer access": "Waiting for customer access", other: "Other",
};
const FIELD_LABELS = { on_site_at: "Arrival", completed_at: "Completion", labour_hours: "Labour hours" };

export const toAstInput = iso => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("sv-SE", { timeZone: "America/Barbados", hour12: false }).slice(0, 16).replace(" ", "T");
};

const pauseMinutes = pause => Math.max(0, Math.floor(
  ((pause.resumed_at ? new Date(pause.resumed_at) : new Date()) - new Date(pause.paused_at)) / 60000,
));

function ManualBadge({ entry, testid }) {
  if (!entry) return null;
  return (
    <span className="bf-chip ml-2" style={{ background: "#FFF6E5", color: "#96650A" }}
          title={`Entered manually by ${entry.by_name}: ${entry.reason}`} data-testid={testid}>
      Manual
    </span>
  );
}

function TimeRow({ label, value, entry, testid }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-[13px]" data-testid={testid}>
      <span style={{ color: "#5B6472" }}>{label}</span>
      <span className="flex items-center">{value}<ManualBadge entry={entry} testid={`${testid}-manual`} /></span>
    </div>
  );
}

function TimesSummary({ job }) {
  const manual = job.times_manual || {};
  const pauses = job.tech_pauses || [];
  return (
    <div data-testid="job-times-summary">
      {job.tech_paused && (
        <span className="bf-chip" style={{ background: "#FFF6E5", color: "#96650A" }} data-testid="job-detail-paused-chip">
          Paused by technician
        </span>
      )}
      <TimeRow label="Arrival" value={fmtTimeAST(job.on_site_at)} entry={manual.on_site_at} testid="job-detail-arrival" />
      <TimeRow label="Completion" value={fmtTimeAST(job.completed_at)} entry={manual.completed_at} testid="job-detail-completion" />
      <TimeRow label="Labour hours" value={`${job.labour_hours ?? 0} h`} entry={manual.labour_hours} testid="job-detail-labour" />
      <TimeRow label="On-site time" value={`${job.on_site_minutes ?? 0} min`} testid="job-detail-on-site-minutes" />
      <TimeRow label="Working time (pauses excluded)" value={`${job.working_minutes ?? 0} min`} testid="job-detail-working-minutes" />
      {Object.entries(manual).map(([key, entry]) => (
        <div key={key} className="text-[12px] mt-1" style={{ color: "#96650A" }} data-testid={`job-detail-manual-note-${key}`}>
          {FIELD_LABELS[key] || key} entered manually by {entry.by_name}: {entry.reason}
        </div>
      ))}
      <div className={`${LABEL} mt-3 mb-1`} style={{ color: "#5B6472" }}>Technician pauses</div>
      {pauses.length === 0 && <div className="text-[12.5px]" style={{ color: "#8A93A1" }}>No pauses</div>}
      {pauses.map((pause, index) => (
        <div key={pause.id || index} className="text-[13px] py-0.5" data-testid={`job-detail-pause-${index}`}>
          Paused: {PAUSE_LABELS[pause.reason] || pause.reason}, {pauseMinutes(pause)} min
          {!pause.resumed_at && <span style={{ color: "#96650A" }}> (still paused)</span>}
        </div>
      ))}
    </div>
  );
}

function EditTimesForm({ job, onSaved, onCancel }) {
  const [arrival, setArrival] = useState(toAstInput(job.on_site_at));
  const [completion, setCompletion] = useState(toAstInput(job.completed_at));
  const [labour, setLabour] = useState(String(job.labour_hours ?? ""));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const hasArrival = Boolean(job.on_site_at) || ["on_site", "tech_complete", "complete"].includes(job.status);
  const hasCompletion = ["tech_complete", "complete"].includes(job.status);

  const save = async () => {
    const body = { reason };
    if (hasArrival && arrival && arrival !== toAstInput(job.on_site_at)) body.on_site_at = arrival;
    if (hasCompletion && completion && completion !== toAstInput(job.completed_at)) body.completed_at = completion;
    if (labour !== "" && Number(labour) !== Number(job.labour_hours ?? 0)) body.labour_hours = labour;
    try {
      setBusy(true);
      const response = await client.patch(`/jobs/${job.id}/times`, body);
      toast.success("Times updated");
      onSaved(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update the times");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="edit-times-form">
      <div className="text-[12px]" style={{ color: "#5B6472" }}>Times are in Barbados time (AST).</div>
      {hasArrival && (
        <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Arrival time</span>
          <input type="datetime-local" className={INPUT} style={{ border: "1px solid #E7EAEE" }}
                 value={arrival} onChange={e => setArrival(e.target.value)} data-testid="edit-times-arrival" />
        </label>
      )}
      {hasCompletion && (
        <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Completion time</span>
          <input type="datetime-local" className={INPUT} style={{ border: "1px solid #E7EAEE" }}
                 value={completion} onChange={e => setCompletion(e.target.value)} data-testid="edit-times-completion" />
        </label>
      )}
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Labour hours</span>
        <input type="number" step="0.01" min="0" max="24" className={INPUT} style={{ border: "1px solid #E7EAEE" }}
               value={labour} onChange={e => setLabour(e.target.value)} data-testid="edit-times-labour" />
      </label>
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Reason (required)</span>
        <Textarea rows={2} className="rounded-[10px] text-[13.5px]" value={reason}
                  onChange={e => setReason(e.target.value)} placeholder="Why are these times being corrected?"
                  data-testid="edit-times-reason" />
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="outline" className="bf-btn-ghost" onClick={onCancel} data-testid="edit-times-cancel">Cancel</Button>
        <Button className="bf-btn-primary px-4" disabled={busy || reason.trim().length < 5} onClick={save}
                data-testid="edit-times-save">Save times</Button>
      </div>
    </div>
  );
}

export function JobTimesDialog({ job, onClose, onDone, onCancel }) {
  const [data, setData] = useState(job);
  const [editing, setEditing] = useState(false);
  useEffect(() => { setData(job); setEditing(false); }, [job]);
  if (!job || !data) return null;
  return (
    <Dialog open onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-[480px] max-h-[88vh] overflow-y-auto" data-testid="job-detail-dialog">
        <DialogHeader>
          <DialogTitle>{data.ticket_number} times</DialogTitle>
          <DialogDescription>Arrival, completion, labour and technician pauses.</DialogDescription>
        </DialogHeader>
        {editing
          ? <EditTimesForm job={data} onCancel={() => setEditing(false)}
                           onSaved={updated => { setData(updated); setEditing(false); onDone(); }} />
          : <TimesSummary job={data} />}
        {!editing && (
          <DialogFooter className="mt-3">
            {onCancel && (
              <Button variant="outline" className="bf-btn-ghost" style={{ color: "#B42318" }} onClick={onCancel}
                data-testid="job-detail-cancel-job">Cancel job</Button>
            )}
            <Button variant="outline" className="bf-btn-ghost" onClick={onClose} data-testid="job-detail-close">Close</Button>
            <Button className="bf-btn-primary px-4" onClick={() => setEditing(true)} data-testid="edit-times-open">Edit times</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
